// Zustand store для управления состоянием таблицы
import { create } from 'zustand';
import type { Row, ColumnConfig } from './types';
import { evaluateFormula } from './formulaEngine';

// Генератор временных ID для новых строк
let tempIdCounter = 0;
const generateTempId = () => `__new_${++tempIdCounter}`;

interface TableState {
  // Исходные данные из БД
  originalData: Row[];

  // Новые строки (ещё не сохранены)
  newRows: Row[];

  // Изменения (rowId -> {field: value})
  changes: Map<string, Partial<Row>>;

  // Строки на удаление
  deletedRows: Set<string>;

  // Ключ строки
  rowKey: string;

  // Конфигурация колонок
  columns: ColumnConfig[];

  // Действия
  setOriginalData: (data: Row[], rowKey: string, columns: ColumnConfig[]) => void;
  updateCell: (rowId: string, field: string, value: any) => void;
  addRow: (row?: Partial<Row>) => string; // возвращает tempId
  addRows: (rows: Partial<Row>[]) => string[]; // bulk add
  removeNewRow: (tempId: string) => void;
  markForDeletion: (rowId: string) => void;
  unmarkForDeletion: (rowId: string) => void;
  resetChanges: () => void;
  getComputedData: () => Row[];
  hasChanges: () => boolean;
  getChangedRows: () => Map<string, Partial<Row>>;
  getNewRows: () => Row[];
  getChangeSet: () => { created: Row[]; updated: Record<string, Partial<Row>>; deleted: string[] };
}

export const useTableStore = create<TableState>((set, get) => ({
  originalData: [],
  newRows: [],
  changes: new Map(),
  deletedRows: new Set(),
  rowKey: 'id',
  columns: [],

  setOriginalData: (data, rowKey, columns) => {
    set({ originalData: data, rowKey, columns, changes: new Map(), newRows: [], deletedRows: new Set() });
  },

  updateCell: (rowId, field, value) => {
    set((state) => {
      // Проверяем, это новая строка или существующая
      const isNewRow = String(rowId).startsWith('__new_');

      if (isNewRow) {
        // Обновляем новую строку по __tempId
        const newRows = state.newRows.map(row =>
          row.__tempId === rowId ? { ...row, [field]: value } : row
        );
        return { newRows };
      } else {
        // Обновляем изменения существующей строки
        const newChanges = new Map(state.changes);
        const rowChanges = newChanges.get(String(rowId)) || {};
        rowChanges[field] = value;
        newChanges.set(String(rowId), rowChanges);
        return { changes: newChanges };
      }
    });
  },

  addRow: (row = {}) => {
    const tempId = generateTempId();
    set((state) => {
      // Используем __tempId для внутренней идентификации, не перезаписываем rowKey
      const newRow = { ...row, __tempId: tempId };
      return { newRows: [...state.newRows, newRow] };
    });
    return tempId;
  },

  addRows: (rows) => {
    const tempIds: string[] = [];
    set((state) => {
      const newRows = rows.map(row => {
        const tempId = generateTempId();
        tempIds.push(tempId);
        // Используем __tempId для внутренней идентификации
        return { ...row, __tempId: tempId };
      });
      return { newRows: [...state.newRows, ...newRows] };
    });
    return tempIds;
  },

  removeNewRow: (tempId) => {
    set((state) => ({
      newRows: state.newRows.filter(row => row.__tempId !== tempId)
    }));
  },

  markForDeletion: (rowId) => {
    set((state) => {
      const newDeleted = new Set(state.deletedRows);
      newDeleted.add(rowId);
      return { deletedRows: newDeleted };
    });
  },

  unmarkForDeletion: (rowId) => {
    set((state) => {
      const newDeleted = new Set(state.deletedRows);
      newDeleted.delete(rowId);
      return { deletedRows: newDeleted };
    });
  },

  resetChanges: () => {
    set({ changes: new Map(), newRows: [], deletedRows: new Set() });
  },

  getComputedData: () => {
    const { originalData, newRows, changes, deletedRows, rowKey, columns } = get();

    const computeRow = (row: Row, rowChanges: Partial<Row> = {}) => {
      const mergedRow = { ...row, ...rowChanges };
      const computedRow = { ...mergedRow };

      columns.forEach((col) => {
        if (col.formula) {
          const value = evaluateFormula(col.formula, mergedRow, columns);
          computedRow[col.field] = value;
        }
      });

      return computedRow;
    };

    // Существующие данные с изменениями (помечаем удалённые)
    const existingData = originalData.map((row) => {
      const id = String(row[rowKey]);
      const rowChanges = changes.get(id) || {};
      const computed = computeRow(row, rowChanges);
      if (deletedRows.has(id)) {
        computed.__deleted = true;
      }
      return computed;
    });

    // Новые строки в начале — сохраняем __tempId для идентификации
    const newData = newRows.map(row => {
      const computed = computeRow(row);
      // __tempId уже есть в row, просто прокидываем
      return computed;
    });

    return [...newData, ...existingData];
  },

  hasChanges: () => {
    const { changes, newRows, deletedRows } = get();
    return changes.size > 0 || newRows.length > 0 || deletedRows.size > 0;
  },

  getChangedRows: () => {
    return new Map(get().changes);
  },

  getNewRows: () => {
    return [...get().newRows];
  },

  getChangeSet: () => {
    const { changes, newRows, deletedRows } = get();

    // Убираем __tempId из новых строк
    const created = newRows.map(row => {
      const { __tempId, ...rest } = row;
      return rest as Row;
    });

    const updated: Record<string, Partial<Row>> = {};
    changes.forEach((value, key) => {
      // Не включаем в updated строки, которые будут удалены
      if (!deletedRows.has(key)) {
        updated[key] = value;
      }
    });

    const deleted = Array.from(deletedRows);

    return { created, updated, deleted };
  },
}));
